/**
 * Persistência de conversas de chat (upsert, unread, listagem, contadores).
 */
import { supabase } from '../../lib/supabase';
import { ChatConversation } from '../../features/chat/hooks/types';
import { isValidJid, generateJidVariants, normalizePhoneForChat } from '../../utils/phoneUtils';
import { mapDbToChatConversation, deduplicateConversations } from './helpers/ChatConversationMapper';
import { fetchEnrichmentMaps } from './helpers/ConversationEnrichmentHelper';
import {
    CONVERSATION_FIELDS,
    DbConversation,
    ConversationCounters,
    PaginatedConversationsResult,
} from './types/ChatConversationTypes';

const EMPTY_COUNTERS: ConversationCounters = { unassigned: 0, mine: 0, all: 0 };
const TABLE = 'chat_conversations';

export class ChatConversationRepository {
    async upsertConversation(
        userId: string,
        connectionId: string,
        conversation: ChatConversation,
        organizationId?: string,
    ): Promise<string | null> {
        try {
            const canonicalJid = conversation.id.startsWith('ig:') ? conversation.id : normalizePhoneForChat(conversation.id);
            if (!isValidJid(canonicalJid)) {
                console.warn('[ChatConvRepo] ID inválido:', conversation.id);
                return null;
            }

            const searchJids = generateJidVariants(canonicalJid);
            const payload = {
                connection_id: connectionId,
                remote_jid: canonicalJid,
                platform: canonicalJid.startsWith('ig:') ? 'instagram' : 'whatsapp',
                contact_name: conversation.name,
                avatar_url: conversation.avatar,
                last_message: conversation.lastMessage,
                last_message_time: conversation.lastMessageTime,
                unread_count: conversation.unreadCount,
                status: 'open' as const,
                updated_at: new Date().toISOString(),
                ...(organizationId && { organization_id: organizationId }),
                ...(conversation.assignedTo ? { assigned_to: conversation.assignedTo } : {}),
            };

            const existing = await this.findLatestByJids(connectionId, searchJids);
            if (existing) {
                await this.updateById(existing.id, payload);
                return existing.id;
            }

            const { data: created, error } = await supabase
                .from(TABLE)
                .insert({
                    ...payload,
                    user_id: userId,
                    // Só atribui se veio explícito (nova conversa no Adlitics).
                    // fromMe/WhatsApp e ensure sem assignedTo → Não atribuídos.
                    assigned_to: conversation.assignedTo ?? null,
                })
                .select('id')
                .single();

            if (error) {
                if (error.code === '23505') {
                    const raced = await this.findLatestByJids(connectionId, searchJids);
                    if (raced) {
                        await this.updateById(raced.id, payload);
                        return raced.id;
                    }
                }
                throw error;
            }
            return created.id;
        } catch (error) {
            console.error('[ChatConvRepo] Erro upsert:', error);
            return null;
        }
    }

    async incrementUnreadCount(conversationDbId: string, lastMessage: string, lastMessageTime: Date): Promise<void> {
        try {
            const { data: conv } = await supabase
                .from(TABLE)
                .select('unread_count')
                .eq('id', conversationDbId)
                .single();
            await this.touchConversation(conversationDbId, {
                unread_count: (conv?.unread_count || 0) + 1,
                last_message: lastMessage,
                last_message_time: lastMessageTime.toISOString(),
            });
        } catch (error) {
            console.error('[ChatConvRepo] Erro incrementUnread:', error);
        }
    }

    async updateLastMessage(conversationDbId: string, lastMessage: string, lastMessageTime: Date): Promise<void> {
        try {
            await this.touchConversation(conversationDbId, {
                last_message: lastMessage,
                last_message_time: lastMessageTime.toISOString(),
            });
        } catch (error) {
            console.error('[ChatConvRepo] Erro updateLastMessage:', error);
        }
    }

    async markAsReadById(conversationDbId: string): Promise<void> {
        await supabase.from(TABLE).update({ unread_count: 0 }).eq('id', conversationDbId);
    }

    async getConversationsPaginated(
        organizationId: string,
        cursor?: string,
        limit = 50,
        connectionId?: string,
    ): Promise<PaginatedConversationsResult> {
        let query = this.openConversationsQuery(organizationId).limit(limit + 1);
        // Inclui connection_id null: órfãs não somem ao filtrar canal.
        if (connectionId) {
            query = query.or(`connection_id.eq.${connectionId},connection_id.is.null`);
        }
        if (cursor) query = query.lt('last_message_time', cursor);

        const { data, error } = await query;
        if (error) throw error;
        if (!data?.length) return { conversations: [], hasMore: false };

        const hasMore = data.length > limit;
        const page = (hasMore ? data.slice(0, limit) : data) as DbConversation[];
        const conversations = await this.hydrateConversations(organizationId, page);
        const last = conversations[conversations.length - 1];

        return {
            conversations,
            hasMore,
            nextCursor: last?.lastMessageTime ? new Date(last.lastMessageTime).toISOString() : undefined,
        };
    }

    async getConversationCounters(
        organizationId: string,
        userId?: string,
        connectionId?: string,
    ): Promise<ConversationCounters> {
        // Soma mensagens não lidas (4+1+1), não quantidade de conversas.
        let query = supabase
            .from(TABLE)
            .select('assigned_to, status, unread_count')
            .eq('organization_id', organizationId)
            .neq('status', 'closed')
            .gt('message_count', 0);
        if (connectionId) {
            query = query.or(`connection_id.eq.${connectionId},connection_id.is.null`);
        }

        const { data, error } = await query;
        if (error || !data?.length) return EMPTY_COUNTERS;

        let unassigned = 0;
        let mine = 0;
        let all = 0;
        for (const c of data) {
            const unread = c.unread_count || 0;
            if (unread <= 0) continue;
            all += unread;
            if (!c.assigned_to) unassigned += unread;
            if (userId && c.assigned_to === userId) mine += unread;
        }
        return { unassigned, mine, all };
    }

    private openConversationsQuery(organizationId: string) {
        return supabase
            .from(TABLE)
            .select(CONVERSATION_FIELDS)
            .eq('organization_id', organizationId)
            .gt('message_count', 0)
            .order('last_message_time', { ascending: false });
    }

    private async hydrateConversations(organizationId: string, rows: DbConversation[]): Promise<ChatConversation[]> {
        const maps = await fetchEnrichmentMaps(organizationId);
        return deduplicateConversations(
            rows.map((row) =>
                mapDbToChatConversation(row, maps.contactIdMap, maps.contactNameMap, maps.contactAvatarMap, maps.leadStageMap),
            ),
        );
    }

    private async findLatestByJids(connectionId: string, searchJids: string[]) {
        const { data } = await supabase
            .from(TABLE)
            .select('id, remote_jid')
            .eq('connection_id', connectionId)
            .in('remote_jid', searchJids)
            .order('last_message_time', { ascending: false })
            .limit(1)
            .maybeSingle();
        return data;
    }

    private async updateById(id: string, payload: Record<string, unknown>) {
        await supabase.from(TABLE).update(payload).eq('id', id);
    }

    private async touchConversation(conversationDbId: string, fields: Record<string, unknown>) {
        await this.updateById(conversationDbId, { ...fields, updated_at: new Date().toISOString() });
    }
}
